import { GOOGLE_ANALYTICS_SCOPES } from 'src/context/api/google/api';
import * as yup from 'yup';

export interface CountryRegionMappingInput {
    region: string;
    countries: string[];
}

export interface CampaignRegionMapping {
    regex: string[];
    region: string;
}

export interface GadsAccountRegionMapping {
    account_id: string;
    region: string;
}

export interface GscCountrySiteUrlMapping {
    countries: string[];
    url: string;
}

export interface SavingsEntry {
    platform: string;
    region_name: string;
    blended_cpc_ref?: number | null;
}

export interface WorkspaceFormData {
    general: {
        name: string;
        url?: string;
        friendlyDomains?: string[];
        template?: string;
        averageCart?: number;
        currency?: string;
        custom?: boolean;
        bqTable?: string;
        shoppingIncluded?: boolean;
        platforms: string[];
    };
    display: {
        showSavings?: boolean;
        brandInfringement?: boolean;
    };
    integrations?: {
        email: string;
        scopes: GOOGLE_ANALYTICS_SCOPES[];
    }[];
    dataConfig: {
        // NON BRAND
        nonbrand?: boolean;
        nonbrand_visibility?: string[];
        nonbrand_include_pmax_campaigns?: boolean;
        // BRAND PULSE
        brandpulse?: boolean;
        brandpulse_visibility?: string[];

        // BRAND PULSE
        conquestmode?: boolean;
        conquestmode_visibility?: string[];

        // ORGANIC CONVERSIONS
        organic_conversion_mode?: 'total' | 'modelized';
        organic_factor?: number | null;
        // LOCAL
        local?: boolean;
        local_visibility?: string[];
        geotargets?: number[];

        selected_countries?: string[];
        branded_keywords?: string[];
        excluded_keywords?: string[];
        exclude_for_non_branded?: string[];
        country_region_mapping?: CountryRegionMappingInput[];
        mapping_type?: 'accounts' | 'campaign_names';
        google_ads?: {
            use_region_mapping?: boolean;
            nonbrand_pmax_campaigns?: string[];
            branded_search_campaigns?: string[];
            competitor_search_campaigns?: string[];
            branded_shopping_campaigns?: string[];
            branded_pmax_campaigns?: string[];
            account_region_mapping?: GadsAccountRegionMapping[];
            campaign_region_mapping?: CampaignRegionMapping[];
            conversion_actions?: string[];
            exclude_non_branded?: boolean;
            customer_id?: {
                id: string;
                name: string;
            }[];
        };
        google_analytics?: {
            property_id?: {
                ga4_property_id: string;
                ga4_property_name: string;
            }[];
            conversion_events?: string[];
        };
        google_search_console?: {
            site_url?: string[];
            country_site_url_mapping?: GscCountrySiteUrlMapping[];
        };
    };
    scraping?: any[];
    savings?: SavingsEntry[];
}

export type StepKey = 'information' | 'settings' | 'scraping' | 'validation';

export const validationSchema = yup.object().shape({
    general: yup.object().shape({
        name: yup.string().required('Name is required').max(255, 'Name must be less than 255 characters'),
        url: yup.string().url('Must be a valid URL').max(255, 'URL must be less than 255 characters'),
        friendlyDomains: yup.array().of(yup.string().required('Friendly domain is required')),
        template: yup.string().max(255, 'Template must be less than 255 characters'),
        averageCart: yup.number().min(0, 'Average cart must be positive'),
        currency: yup.string().max(255, 'Currency must be less than 255 characters'),
        custom: yup.boolean(),
        bqTable: yup.string().max(255, 'BigQuery table must be less than 255 characters'),
        shoppingIncluded: yup.boolean(),
        platforms: yup.array().of(yup.string().oneOf(['Google', 'Bing'])).min(1, 'At least one platform is required'),
    }),
    display: yup.object().shape({
        showSavings: yup.boolean(),
        brandInfringement: yup.boolean(),
    }),
    dataConfig: yup.object().shape({
        nonbrand: yup.boolean(),
        nonbrand_visibility: yup.array().of(yup.string().required()),
        nonbrand_include_pmax_campaigns: yup.boolean(),
        local: yup.boolean(),
        local_visibility: yup.array().of(yup.string().required()),
        geotargets: yup.array().of(yup.number().required()),
        selected_countries: yup.array().of(yup.string().required('Country is required')),
        branded_keywords: yup.array().of(yup.string().required('Keyword is required')),
        excluded_keywords: yup.array().of(yup.string().required('Keyword is required')),
        exclude_for_non_branded: yup.array().of(yup.string().required('Keyword is required')),

        organic_conversion_mode: yup
            .mixed<'total' | 'modelized'>()
            .oneOf(['total', 'modelized'])
            .required(),

        organic_factor: yup
            .number()
            .nullable()
            .when('organic_conversion_mode', {
                is: 'modelized',
                then: (schema) =>
                    schema
                        .required('Factor is required')
                        .min(0.01, 'Factor must be between 0.01 and 0.99')
                        .max(0.99, 'Factor must be between 0.01 and 0.99'),
                otherwise: (schema) => schema.nullable(),
            }),

        country_region_mapping: yup.array().of(
            yup.object().shape({
                region: yup.string().required('Region is required'),
                countries: yup.array().of(yup.string()).required(),
            })
        ),
        mapping_type: yup
            .mixed<'accounts' | 'campaign_names'>()
            .oneOf(['accounts', 'campaign_names'])
            .required('Mapping type is required'),
        google_ads: yup.object().shape({
            nonbrand_pmax_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            branded_search_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            competitor_search_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            branded_shopping_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            branded_pmax_campaigns: yup.array().of(yup.string().required('Campaign is required')),
            account_region_mapping: yup.array().of(
                yup.object().shape({
                    account_id: yup.string().required('Account is required'),
                    region: yup.string().required('Region is required'),
                })
            ),
            campaign_region_mapping: yup.array().of(
                yup.object().shape({
                    regex: yup.array().of(yup.string().required('Regex is required')),
                    region: yup.string().required('Region is required'),
                })
            ),
            conversion_actions: yup.array().of(yup.string().required('Conversion action is required')),
            exclude_non_branded: yup.boolean(),
            customer_id: yup.array().of(yup.object().shape({
                id: yup.string().required('Customer ID is required'),
                name: yup.string().required('Customer name is required'),
            })),
        }),
        google_search_console: yup.object().shape({
            country_site_url_mapping: yup.array().of(
                yup.object().shape({
                    countries: yup.array().of(yup.string().required('Country is required')),
                    url: yup.string().required('URL is required'),
                })
            ),
        }),
        google_analytics: yup.object().shape({
            property_id: yup.array().of(yup.object().shape({
                ga4_property_id: yup.string().required('Property ID is required'),
                ga4_property_name: yup.string().required('Property name is required'),
            })),
            conversion_events: yup.array().of(yup.string().required('Conversion event is required')),
        }),
    }),
});

export const emptyValues: WorkspaceFormData = {
    general: {
        name: '',
        url: '',
        friendlyDomains: [],
        template: '',
        averageCart: 0,
        currency: 'usd',
        custom: false,
        bqTable: '',
        shoppingIncluded: false,
        platforms: [],
    },
    display: {
        showSavings: true,
        brandInfringement: false,
    },
    dataConfig: {
        nonbrand: false,
        nonbrand_visibility: ["brand specialist", "admin", "consultant"],
        nonbrand_include_pmax_campaigns: false,
        local: false,
        local_visibility: ["brand specialist", "admin", "consultant"],
        geotargets: [],
        selected_countries: [],

        organic_conversion_mode: 'total',
        organic_factor: null,

        branded_keywords: [],
        excluded_keywords: [],
        exclude_for_non_branded: [],
        country_region_mapping: [],
        mapping_type: undefined,
        google_ads: {
            use_region_mapping: false,
        },
        google_analytics: {},
        google_search_console: {},
    },
    scraping: [],
    savings: [],
};


